// CMD-Bridge Frontend Application
// Build: 2026-02-04 - Error reporting enabled
// Trigger rebuild: 2026-03-02
import React from 'react';
import { MotionConfig } from 'framer-motion';
import ReactDOM from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import { App as CapacitorApp } from '@capacitor/app';
import { router } from './App';
import './index.css';
import { initializeErrorReporter } from './utils/error-reporter';
import { forceReloadOnce, CHUNK_RELOAD_GUARD_KEY } from './utils/lazyWithRetry';
import { queryClient } from './config/queryClient';
import { networkService } from './services/network.service';
import { localNotificationService } from './services/local-notification.service';
import { fileStorageService } from './services/file-storage.service';
import { storageQuotaService } from './offline/storage-quota.service';
import { syncOrchestratorService } from './offline/sync-orchestrator.service';
import { liveUpdateService } from './services/live-update.service';
import { nativePushService } from './services/native-push.service';
import {
  hydrateNativeAuthToken,
  NATIVE_HYDRATION_TIMEOUT_MS,
  retryNativeAuthHydration,
  startNativeAuthTokenPersistence,
  withStartupTimeout,
} from './services/auth-token-persistence';
import { deviceSessionService } from './services/device-session.service';

// NOTE: ArcGIS SDK CSS + API key setup intentionally does NOT live here.
// Importing anything from @arcgis/core in this eager entry graph pulls the
// entire arcgis-core chunk (~3.9 MB gzip) into first paint. The SDK is
// bootstrapped by src/utils/arcgis-bootstrap.ts, which every MapView/
// SceneView-creating component imports for its side effects.

const isNative = Capacitor.isNativePlatform();

// Capacitor 8 edge-to-edge mode (default on Android 15+) reports the system
// nav inset through env(safe-area-inset-bottom) correctly, so we just mirror
// that into a CSS variable everywhere — same on web, iOS, Android. The
// earlier Android-specific JS calculation (with a 48px floor) was overshooting
// on modern devices because it always returned at least 48px even when the
// actual gesture-nav inset was smaller.
//
// If you ever see content rendered UNDER the system nav bar on an older
// Android device where env() reports 0, the fallback is the literal "0px" at
// the end of env() — bump that to a non-zero value here, don't reintroduce
// the JS measurement.
document.documentElement.style.setProperty(
  '--safe-area-bottom',
  'env(safe-area-inset-bottom, 0px)'
);

// Handle Capacitor app lifecycle events
if (isNative) {
  // Live web-bundle updates. Must run first on every launch: it confirms this
  // bundle booted (or the updater rolls it back) and swaps in an update that
  // was downloaded last session before the user starts working.
  // OTA stays cold-start-only: onLaunch() applies a bundle downloaded last
  // session before anything renders; onResume() below only downloads.
  void liveUpdateService.onLaunch();

  // Handle app resume from background.
  // On Android the WebView's visibilitychange event is unreliable, so we use
  // Capacitor's appStateChange to:
  //   1. Tell React Query the window "focused" so stale queries refetch.
  //   2. Nudge any idle socket.io connections that may have silently dropped.
  CapacitorApp.addListener('appStateChange', ({ isActive }) => {
    if (isActive) {
      // Trigger React Query's refetchOnWindowFocus behaviour
      queryClient.getQueryCache().getAll().forEach((query) => {
        if (query.isStale()) {
          query.fetch();
        }
      });

      // Dispatch a synthetic focus event so any listener-based reconnect
      // logic (socket.io, network service) fires as well.
      window.dispatchEvent(new Event('focus'));

      // App resumed — flush any sync queue that built up while backgrounded.
      void syncOrchestratorService.syncNow();

      // Throttled check for a newer web bundle (applies on next cold start).
      liveUpdateService.onResume();

      // Re-read the token vault if the Keystore was busy at start-up (P1-12).
      // A no-op once hydration has settled.
      void retryNativeAuthHydration();

      // Retry a device-session registration that failed earlier (offline at
      // sign-in). A no-op once the token is bound to this device.
      void deviceSessionService.ensure();
    } else {
      // App moving to background — opportunistically kick a sync so pending
      // writes flush before the OS suspends the WebView. This is best-effort:
      // the OS may suspend us before the network requests complete. For true
      // background-survival on long sessions, install @capacitor/background-task
      // and wrap this in BackgroundTask.beforeExit(...).
      void syncOrchestratorService.syncNow();
    }
  });

  // Handle deep links into the WebView.
  //
  // Two sources of inbound URLs:
  //   1. Android App Links / iOS Universal Links — verified https URLs like
  //      `https://app.cmd-bridge.com/auth/magic?token=...` that the OS routes
  //      to the app directly (no browser hop). These keep their full pathname.
  //   2. Custom-scheme URLs — `com.cmdbridge.app.us://callback?code=...`. Legacy
  //      Auth0 callback, kept for backward compat. `new URL()` puts the host
  //      part in `.host`, not `.pathname`, so we check both.
  //
  // The WebView's origin is whatever the app loaded (https://localhost in dev,
  // app.cmd-bridge.com in prod). We navigate by setting window.location.href
  // to a path-only string so React Router picks it up.
  const APP_LINK_PATHS = [
    '/auth/magic',
    '/auth/reset-password',
    '/auth/verify-email',
    '/auth/sso-callback',
    '/auth/magic-request',
    '/invitations/',
    '/setup-account/',
    // Push notification deep links — must match common notification target
    // paths the backend emits in notification.data.url. Server-side
    // apple-app-site-association and assetlinks.json must include the same
    // paths for true App Link verification (deployed separately).
    '/incidents/',
    '/field/',
    '/tasks/',
    '/ics214/',
    '/forms/',
    '/damage-assessments/',
    '/board/',
    '/messages/',
    '/notifications/',
  ];

  CapacitorApp.addListener('appUrlOpen', (event) => {
    let target: string | null = null;
    try {
      const url = new URL(event.url);
      // Custom scheme fallback (legacy Auth0).
      if (url.host === 'callback' || url.pathname.startsWith('/callback')) {
        target = '/callback' + url.search;
      } else if (APP_LINK_PATHS.some((p) => url.pathname.startsWith(p))) {
        // App Link / Universal Link — preserve full path + query + hash.
        target = url.pathname + url.search + url.hash;
      }
    } catch {
      // Malformed URL — ignore.
      return;
    }
    if (target) {
      // Use href so React Router's HistoryRouter picks up the change as a
      // navigation event rather than treating it as a same-page hash update.
      window.location.href = target;
    }
  });

  // Handle Android back button
  CapacitorApp.addListener('backButton', ({ canGoBack }) => {
    if (canGoBack) {
      window.history.back();
    } else {
      CapacitorApp.exitApp();
    }
  });
}

// React Query client imported from config/queryClient.ts

// Service Worker Registration - only for web, not native apps
// Native apps use Capacitor plugins for offline/push functionality
async function handleServiceWorker() {
  if ('serviceWorker' in navigator) {
    if (isNative) {
      // Unregister service worker in native apps to prevent caching conflicts
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.unregister();
        console.log('[App] Service worker unregistered for native platform');
      }
      // Clear all caches to ensure fresh content from app bundle
      const cacheNames = await caches.keys();
      await Promise.all(cacheNames.map((name) => caches.delete(name)));
      console.log('[App] Caches cleared for native platform');
    } else {
      // Register service worker for web only
      const { registerSW } = await import('virtual:pwa-register');
      const updateSW = registerSW({
        immediate: true,
        onRegistered(registration) {
          console.log('[App] Service worker registered:', registration);
          // Check for updates periodically (every 5 minutes)
          if (registration) {
            setInterval(async () => {
              try {
                // Only attempt update if service worker is in a valid state
                if (registration.active && !registration.installing && !registration.waiting) {
                  await registration.update();
                  console.log('[App] Service worker update check completed');
                }
              } catch (error) {
                // InvalidStateError can occur during race conditions - not critical
                // Log but don't propagate as it will retry on next interval
                console.debug('[App] Service worker update check skipped:', error);
              }
            }, 5 * 60 * 1000);
          }
        },
        onRegisterError(error) {
          console.error('[App] Service worker registration error:', error);
        },
        onNeedRefresh() {
          // New content available - the ServiceWorkerUpdateBanner component
          // handles the UI notification via useServiceWorker hook
          console.log('[App] New content available, update banner will show');
        },
        onOfflineReady() {
          console.log('[App] App is ready for offline use');
        },
      });
    }
  }
}

// Auto-reload on stale chunk errors after deployments.
// Vite fires this when a dynamic import() fails (e.g. old hashed chunk removed).
// We reload once; a sessionStorage flag (shared with lazyWithRetry) prevents
// infinite loops.
//
// IMPORTANT: only call preventDefault() when we actually initiate a reload.
// Vite's __vitePreload makes a prevented import RESOLVE with `undefined`
// instead of rejecting, which caused "Cannot read properties of undefined
// (reading 'IncidentList')" TypeErrors in lazy-route mappers (issue #737).
// When the guard is already set, we let the real error propagate so
// lazyWithRetry / ErrorBoundary can handle it with accurate error info.
window.addEventListener('vite:preloadError', (event) => {
  if (forceReloadOnce()) {
    // Reload is in flight — suppress the rethrow to avoid an ErrorBoundary
    // flash and a bogus error report for a self-healing condition.
    event.preventDefault();
  }
});

// Clear the reload guard only after the app has run healthily for a while, so
// future deploys can still trigger an auto-reload. Clearing immediately on
// load would defeat the loop protection (a persistently stale index.html
// would reload forever).
window.setTimeout(() => {
  try {
    sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
  } catch {
    // sessionStorage may be unavailable in some embedded WebViews; ignore.
  }
}, 30_000);

// One-shot cleanup: remove stale Auth0 SPA SDK keys left in localStorage from
// pre-Phase-6 sessions. Auth0 was retired (see CLAUDE.md "Authentication"),
// but cached tokens / user blobs under `@@auth0spajs@@::...` linger in users'
// browsers and surface in QA as "Auth0 still active". This is a safe wipe —
// the keys are no longer read by any code path.
try {
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('@@auth0spajs@@')) {
      localStorage.removeItem(key);
    }
  }
} catch {
  // localStorage may be unavailable in some embedded WebViews; ignore.
}

/**
 * Native: the session token lives in the Keystore vault (P1-12), so it is read
 * (and, on the first launch of this build, migrated out of the old plaintext
 * storage) before startApp() sends authenticated requests.
 *
 * The wait is bounded (NATIVE_HYDRATION_TIMEOUT_MS): a plugin call that never
 * answers must not leave the life-safety app on a blank screen with SOS out of
 * reach. On a timeout the app renders anyway; hydration keeps running and its
 * result applies when it arrives, and until then the API client does not
 * treat a 401 as a sign-out (native-auth-hydration-state.ts).
 */
let nativeSessionReady: Promise<unknown> = Promise.resolve();
if (isNative) {
  const nativeSessionWork = (async () => {
    await hydrateNativeAuthToken();
    startNativeAuthTokenPersistence();
    // Bind the session to this device (POST /api/auth/device/register): typed
    // terminal codes, a revocable session, and the tracker credential.
    deviceSessionService.start();
    // Register this device for native push at app start (P0-20), not only from
    // Account Settings: an SOS or evacuation push cannot reach a phone that
    // never registered. The service follows the signed-in session
    // (rehydration, sign-in, sign-out) and owns the push listeners for the
    // app's lifetime.
    nativePushService.start();
  })();
  nativeSessionReady = withStartupTimeout(nativeSessionWork, NATIVE_HYDRATION_TIMEOUT_MS, () =>
    console.warn('[App] token vault slow to answer; rendering before it')
  );
}

function startApp(): void {
  // Initialize error reporter for automatic error tracking
  initializeErrorReporter();

  // Initialize network connectivity detection (Capacitor native or web fallback)
  networkService.init();

  // Initialize local notifications for native platforms (permissions + sync alerts)
  localNotificationService.init();

  // Initialize native filesystem for offline photo storage (creates directory structure)
  fileStorageService.init();

  // Start monitoring IndexedDB quota — iOS WebView evicts under storage pressure
  // without warning. We preemptively purge cached blobs before that happens.
  storageQuotaService.startMonitoring();

  // Wire up the global reconnect-triggered sync. Listens to network events
  // app-wide so pending writes flush as soon as connectivity returns, even
  // when the user isn't on a field page where SyncStatusIndicator is mounted.
  syncOrchestratorService.init();

  // Bridge drain (S-06, migration plan §6.2): flush safety items first, keep
  // an encrypted handoff for the MAUI app, report drain_state. Android shell
  // only, and loaded lazily so the web console never downloads it.
  if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'android') {
    void import('./offline/drain/bridge-drain-runtime')
      .then((m) => m.startBridgeDrain())
      .then(async (started) => {
        if (started) (await import('./components/drain/SyncBeforeUpdateGate')).mountSyncBeforeUpdateGate();
      })
      .catch((err) => console.warn('[BridgeDrain] not started:', err));
  }

  // Initialize service worker handling
  handleServiceWorker();

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      {/*
        reducedMotion="user" makes every Framer Motion animation honour the OS
        "reduce motion" setting.

        index.css already neutralises CSS animations and transitions under
        `@media (prefers-reduced-motion: reduce)`, but Framer Motion animates from
        JavaScript and that media query does not touch it. 14 components check the
        preference themselves; 7 did not -- including six auth screens, which are
        the first thing anyone sees. Setting it once here covers those, and every
        motion component added later, without each having to remember.
      */}
      <MotionConfig reducedMotion="user">
        <QueryClientProvider client={queryClient}>
          <RouterProvider router={router} />
        </QueryClientProvider>
      </MotionConfig>
    </React.StrictMode>
  );
}

void nativeSessionReady
  .catch((err) => console.error('[App] native session preparation failed', err))
  .finally(startApp);
